import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Alert,
  LinearProgress,
  Grid,
} from '@mui/material';
import { useSelector } from 'react-redux';

export default function ManualRun() {
  const { token } = useSelector((state) => state.auth);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingWb, setLoadingWb] = useState(false);
  const [loadingOzon, setLoadingOzon] = useState(false);
  const [loadingTech, setLoadingTech] = useState(false);
  const [loadingYa, setLoadingYa] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loadingFbs, setLoadingFbs] = useState(false);
  const [fbsInfo, setFbsInfo] = useState({ wb: null, ozon: null, summary: null });
  const [techProgress, setTechProgress] = useState(null);
  const [productsProgress, setProductsProgress] = useState(null);
  const [loadingForceReload, setLoadingForceReload] = useState(false);
  const [loadingWbSupplies, setLoadingWbSupplies] = useState(false);
  const [loadingWbStatOrders, setLoadingWbStatOrders] = useState(false);
  const [loadingWbStatSales, setLoadingWbStatSales] = useState(false);

  const fmtYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const nowDate = new Date();
  const weekAgoDate = new Date(nowDate);
  weekAgoDate.setDate(nowDate.getDate() - 7);
  const [wbSupFrom, setWbSupFrom] = useState(fmtYmd(weekAgoDate));
  const [wbSupTo, setWbSupTo] = useState(fmtYmd(nowDate));
  const [wbStatFrom, setWbStatFrom] = useState(fmtYmd(weekAgoDate));

  const API_URL = import.meta.env.VITE_API_URL || '/api';

  const runProducts = async () => {
    setLoadingProducts(true);
    setMessage(null);
    setError(null);
    setProductsProgress('Запускаем синхронизацию…');
    try {
      const res = await fetch(`${API_URL}/ms/sync-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409 && json?.details?.job_id) {
        const done = await waitMsProductsJob(json.details);
        if (done.status === 'failed') throw new Error(done.error_message || 'Ошибка синхронизации');
        setMessage(done.result ? 'Синхронизация товаров завершена' : (json.message || 'Готово'));
        return;
      }
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      if (json.job_id) {
        const done = await waitMsProductsJob(json);
        if (done.status === 'failed') throw new Error(done.error_message || 'Ошибка синхронизации');
        setMessage('Синхронизация товаров завершена');
        return;
      }
      setMessage(json.message || 'Загрузка товаров запущена');
    } catch (err) {
      setError(err.message);
    } finally {
      setProductsProgress(null);
      setLoadingProducts(false);
    }
  };

  const waitMsProductsJob = async (job) => {
    const id = String(job?.job_id || '').trim();
    if (!id) throw new Error('Не удалось запустить синхронизацию');
    const poll = async () => {
      const res = await fetch(`${API_URL}/ms/jobs/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        throw new Error('Задание прервалось после перезапуска сервера. Запустите снова.');
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Ошибка синхронизации (${res.status})`);
      setProductsProgress(data.message || 'Загружаем товары из МойСклад…');
      if (data.status === 'completed' || data.status === 'failed') return data;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return poll();
    };
    return poll();
  };

  const runWb = async () => {
    setLoadingWb(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/ms/wb/sync-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      const total = (json.result?.accounts || []).reduce((s, a) => s + (a.processed || 0), 0);
      setMessage(`${json.message}. Обработано: ${total}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingWb(false);
    }
  };

  const runOzon = async () => {
    setLoadingOzon(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/ms/ozon/sync-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      const total = (json.result?.accounts || []).reduce((s, a) => s + (a.processed || 0), 0);
      setMessage(`${json.message}. Обработано: ${total}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingOzon(false);
    }
  };

  const runYandex = async () => {
    setLoadingYa(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/ms/yandex/sync-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      const total = (json.result?.accounts || []).reduce((s, a) => s + (a.processed || 0), 0);
      setMessage(`${json.message}. Обработано: ${total}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingYa(false);
    }
  };

  const runTechkarty = async () => {
    setLoadingTech(true);
    setMessage(null);
    setError(null);
    setTechProgress(null);
    try {
      const res = await fetch(`${API_URL}/techkarty/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const reader = res.body?.getReader();
      if (!reader) {
        const fallback = await res.text();
        throw new Error(fallback?.trim() || 'Пустой ответ сервера');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;
      let fallbackMessage = null;

      const handleEvent = (event) => {
        if (!event || typeof event !== 'object') return;
        const { type } = event;
        if (type === 'start') {
          setTechProgress('Синхронизация запущена…');
          return;
        }
        if (type === 'progress') {
          const p = event.progress || {};
          const parts = [];
          if (Number.isFinite(p.batch)) parts.push(`пакет ${p.batch}`);
          if (Number.isFinite(p.batchSize)) parts.push(`строк: ${p.batchSize}`);
          if (Number.isFinite(p.offset)) parts.push(`offset: ${p.offset}`);
          if (Number.isFinite(p.processedTechkarty)) parts.push(`техкарт всего: ${p.processedTechkarty}`);
          if (Number.isFinite(p.processedMaterials)) parts.push(`материалов всего: ${p.processedMaterials}`);
          if (Number.isFinite(event.elapsedMs)) {
            const seconds = event.elapsedMs / 1000;
            parts.push(`время: ${seconds.toFixed(seconds >= 10 ? 0 : 1)} с`);
          }
          setTechProgress(parts.length ? `Прогресс — ${parts.join(', ')}` : 'Прогресс обновлён…');
          return;
        }
        if (type === 'result' || typeof event.success === 'boolean') {
          finalResult = {
            success: event.success,
            message: event.message,
            processedTechkarty: event.processedTechkarty,
            processedMaterials: event.processedMaterials,
            batches: event.batches,
            durationMs: event.durationMs,
          };
          return;
        }
        if (typeof event.message === 'string' && !fallbackMessage) {
          fallbackMessage = event.message;
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);
          if (!line) continue;
          try {
            const parsed = JSON.parse(line);
            handleEvent(parsed);
          } catch (parseErr) {
            console.warn('techkarty progress parse error:', parseErr, line);
          }
        }
      }

      buffer += decoder.decode();
      const tail = buffer.trim();
      if (tail) {
        try {
          const parsed = JSON.parse(tail);
          handleEvent(parsed);
        } catch (parseErr) {
          console.warn('techkarty tail parse error:', parseErr, tail);
        }
      }

      if (!finalResult) {
        if (fallbackMessage) throw new Error(fallbackMessage);
        if (!res.ok) throw new Error(`Ошибка синхронизации (${res.status})`);
        throw new Error('Не удалось получить результат синхронизации');
      }
      if (!finalResult.success) {
        throw new Error(finalResult.message || 'Ошибка синхронизации');
      }

      const parts = [];
      if (Number.isFinite(finalResult.processedTechkarty)) parts.push(`техкарт: ${finalResult.processedTechkarty}`);
      if (Number.isFinite(finalResult.processedMaterials)) parts.push(`материалов: ${finalResult.processedMaterials}`);
      if (Number.isFinite(finalResult.batches)) parts.push(`батчей: ${finalResult.batches}`);
      if (Number.isFinite(finalResult.durationMs)) {
        const seconds = finalResult.durationMs / 1000;
        parts.push(`время: ${seconds.toFixed(seconds >= 10 ? 0 : 1)} с`);
      }
      const suffix = parts.length ? ` (${parts.join(', ')})` : '';
      setMessage((finalResult.message || 'Синхронизация техкарт завершена') + suffix);
      setTechProgress(null);
    } catch (err) {
      setError(err.message);
      setTechProgress(null);
    } finally {
      setLoadingTech(false);
    }
  };

  const runFbs = async () => {
    setLoadingFbs(true);
    setMessage(null);
    setError(null);
    setFbsInfo({ wb: null, ozon: null, summary: null });
    try {
      const res = await fetch(`${API_URL}/ms/refresh-fbs-barcodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ since: '2025-08-17 00:00:00' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      const wbMsg = json?.wb_message || `WB: обновлено ${json?.result?.wb?.updated ?? 0}`;
      const ozMsg = json?.ozon_message || `Ozon: обновлено ${json?.result?.ozon?.updated ?? 0}`;
      setFbsInfo({ wb: wbMsg, ozon: ozMsg, summary: json?.message || 'ШК FBS обновлены' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingFbs(false);
    }
  };

  const runWbSupplies = async () => {
    setLoadingWbSupplies(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/analytics/supplies/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date_from: wbSupFrom, date_to: wbSupTo }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      setMessage(json.message || 'Синхронизация поставок WB завершена');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingWbSupplies(false);
    }
  };

  const runWbStatOrders = async () => {
    setLoadingWbStatOrders(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/analytics/wb-stat/orders/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date_from: wbStatFrom }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      setMessage(json.message || 'Загрузка заказов WB завершена');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingWbStatOrders(false);
    }
  };

  const runWbStatSales = async () => {
    setLoadingWbStatSales(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/analytics/wb-stat/sales/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date_from: wbStatFrom }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка запуска');
      setMessage(json.message || 'Загрузка продаж WB завершена');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingWbStatSales(false);
    }
  };

  const runForceReload = async () => {
    setLoadingForceReload(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/settings/force-reload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Ошибка');
      setMessage('Всем пользователям будет показано уведомление об обновлении');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingForceReload(false);
    }
  };

  const isAnyLoading = loadingProducts || loadingWb || loadingOzon || loadingTech || loadingYa || loadingFbs || loadingWbSupplies || loadingWbStatOrders || loadingWbStatSales || loadingForceReload;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Ручной запуск
      </Typography>

      {isAnyLoading && <LinearProgress sx={{ mb: 2 }} />}

      {(message || error) && (
        <Box sx={{ mb: 2 }}>
          {message && <Alert severity="success">{message}</Alert>}
          {error && <Alert severity="error" sx={{ mt: message ? 1 : 0 }}>{error}</Alert>}
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Синхронизация товаров
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={runProducts} disabled={loadingProducts}>
            {loadingProducts ? 'Обработка…' : 'МойСклад'}
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#6E0AEF', '&:hover': { bgcolor: '#5b09c6' } }}
            onClick={runWb}
            disabled={loadingWb}
          >
            {loadingWb ? 'Обработка…' : 'Wildberries'}
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#005BFF', '&:hover': { bgcolor: '#0048cc' } }}
            onClick={runOzon}
            disabled={loadingOzon}
          >
            {loadingOzon ? 'Обработка…' : 'Ozon'}
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#FC3F1D', '&:hover': { bgcolor: '#e3381a' } }}
            onClick={runYandex}
            disabled={loadingYa}
          >
            {loadingYa ? 'Обработка…' : 'Yandex'}
          </Button>
        </Box>
        {productsProgress && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {productsProgress}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Техкарты и ШК FBS
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" onClick={runTechkarty} disabled={loadingTech}>
            {loadingTech ? 'Обработка…' : 'Загрузка техкарт'}
          </Button>
          <Button variant="contained" color="secondary" onClick={runFbs} disabled={loadingFbs}>
            {loadingFbs ? 'Обработка…' : 'Обновить ШК FBS'}
          </Button>
        </Box>
        {techProgress && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {techProgress}
          </Typography>
        )}
        {(fbsInfo.summary || fbsInfo.wb || fbsInfo.ozon) && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            {fbsInfo.summary && <Typography variant="subtitle2">{fbsInfo.summary}</Typography>}
            {fbsInfo.wb && <Typography variant="body2" sx={{ color: '#6E0AEF' }}>{fbsInfo.wb}</Typography>}
            {fbsInfo.ozon && <Typography variant="body2" sx={{ color: '#005BFF' }}>{fbsInfo.ozon}</Typography>}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Поставки WB
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              size="small"
              type="date"
              label="С"
              InputLabelProps={{ shrink: true }}
              value={wbSupFrom}
              onChange={(e) => setWbSupFrom(e.target.value)}
              sx={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <TextField
              size="small"
              type="date"
              label="По"
              InputLabelProps={{ shrink: true }}
              value={wbSupTo}
              onChange={(e) => setWbSupTo(e.target.value)}
              sx={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ bgcolor: '#6E0AEF', '&:hover': { bgcolor: '#5b09c6' } }}
              onClick={runWbSupplies}
              disabled={loadingWbSupplies}
            >
              {loadingWbSupplies ? 'Загрузка...' : 'Загрузить поставки'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Заказы и продажи WB (статистика)
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              size="small"
              type="date"
              label="С"
              InputLabelProps={{ shrink: true }}
              value={wbStatFrom}
              onChange={(e) => setWbStatFrom(e.target.value)}
              sx={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ bgcolor: '#6E0AEF', '&:hover': { bgcolor: '#5b09c6' } }}
              onClick={runWbStatOrders}
              disabled={loadingWbStatOrders}
            >
              {loadingWbStatOrders ? 'Загрузка...' : 'Загрузить заказы'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ bgcolor: '#6E0AEF', '&:hover': { bgcolor: '#5b09c6' } }}
              onClick={runWbStatSales}
              disabled={loadingWbStatSales}
            >
              {loadingWbStatSales ? 'Загрузка...' : 'Загрузить продажи'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
          Управление клиентами
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Показать всем активным пользователям диалог с требованием обновить страницу
        </Typography>
        <Button
          variant="contained"
          color="warning"
          onClick={runForceReload}
          disabled={loadingForceReload}
        >
          {loadingForceReload ? 'Отправка…' : 'Принудительное обновление'}
        </Button>
      </Paper>
    </Box>
  );
}
